export const dateCorrectFallback = (
  selectedDate?: string,
  creationTimestamp?: string
): boolean | null => {
  if (!selectedDate || !creationTimestamp) return null
  const singaporeDay = new Date(new Date(creationTimestamp).getTime() + 8 * 3600 * 1000)
    .toISOString()
    .slice(0, 10)
  return selectedDate.slice(0, 10) === singaporeDay
}

const jaroWinkler = (first: string, second: string): number => {
  const s = first.toLowerCase()
  const t = second.toLowerCase()
  if (s === t) return 1
  if (!s.length || !t.length) return 0

  const matchDistance = Math.max(Math.floor(Math.max(s.length, t.length) / 2) - 1, 0)
  const sMatches = new Array(s.length).fill(false)
  const tMatches = new Array(t.length).fill(false)
  let matches = 0

  for (let i = 0; i < s.length; i++) {
    const start = Math.max(0, i - matchDistance)
    const end = Math.min(t.length, i + matchDistance + 1)
    for (let j = start; j < end; j++) {
      if (tMatches[j] || s[i] !== t[j]) continue
      sMatches[i] = tMatches[j] = true
      matches++
      break
    }
  }
  if (matches === 0) return 0

  let transpositions = 0
  let k = 0
  for (let i = 0; i < s.length; i++) {
    if (!sMatches[i]) continue
    while (!tMatches[k]) k++
    if (s[i] !== t[k]) transpositions++
    k++
  }
  transpositions = Math.floor(transpositions / 2)

  const jaro =
    (matches / s.length + matches / t.length + (matches - transpositions) / matches) / 3

  let prefix = 0
  for (let i = 0; i < Math.min(4, s.length, t.length); i++) {
    if (s[i] === t[i]) prefix++
    else break
  }
  return jaro + prefix * 0.1 * (1 - jaro)
}

const levenshteinSimilar = (s: string, t: string, threshold: number): boolean => {
  const m = s.length
  const n = t.length
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  )
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + (s[i - 1] === t[j - 1] ? 0 : 1)
      )
    }
  }
  return 1 - dp[m][n] / Math.max(m, n) >= threshold
}

const WORD_LISTS: Record<string, { answers: string[]; threshold: number; charBased: boolean }> = {
  en: { answers: ['Lemon', 'Key', 'Balloon'], threshold: 0.85, charBased: false },
  zh: { answers: ['柠檬', '锁匙', '气球'], threshold: 0.5, charBased: true },
  ms: { answers: ['Lemon', 'Kunci', 'Belon'], threshold: 0.85, charBased: false },
}

const checkRecalledWords = (
  recalled: string[],
  { answers, threshold, charBased }: (typeof WORD_LISTS)['en']
): boolean => {
  const remaining = [...answers]
  for (const word of recalled) {
    const index = remaining.findIndex((answer) =>
      charBased ? levenshteinSimilar(word, answer, threshold) : jaroWinkler(word, answer) >= threshold
    )
    if (index === -1) return false
    remaining.splice(index, 1)
  }
  return true
}

const oneTextboxCorrect = (raw: string, language: string): boolean => {
  const trimmed = raw.trim()
  if (!trimmed) return false

  if (language === 'zh') {
    const han = trimmed
      .replace(/[^一-鿿]/g, '')
      .replace(/钥匙/g, '锁匙')
      .replace(/汽球/g, '气球')
    return han.includes('柠檬') && han.includes('锁匙') && han.includes('气球')
  }

  const tokens = trimmed.toLowerCase().split(/[^a-z]+/).filter(Boolean)
  const isMs = language === 'ms'
  const lemonVariants = ['lemon', 'lemons', 'lemmon', 'lemoon', 'lemin', 'leomn', 'lemn']
  const keyVariants = isMs ? ['kunci', 'kuncis'] : ['key', 'keys', 'kei', 'kay']
  const balloonVariants = ['balloon', 'balloons', 'baloon', 'ballon', 'balon']

  return (
    tokens.some((token) => lemonVariants.includes(token)) &&
    tokens.some((token) => keyVariants.includes(token)) &&
    tokens.some((token) => balloonVariants.includes(token))
  )
}

export const wordsCorrectFallback = (record: any): boolean | null => {
  if (!record) return null
  const language = String(record.otherInformation?.i18n ?? 'en')

  if (typeof record.recalledThreeWordsInOneTextbox === 'string') {
    return oneTextboxCorrect(record.recalledThreeWordsInOneTextbox, language)
  }
  if (Array.isArray(record.recalledThreeWords)) {
    const wordList = WORD_LISTS[language]
    if (!wordList) return false
    return checkRecalledWords(record.recalledThreeWords, wordList)
  }
  return null
}
